import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Stack, TextField, Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useIsAdmin } from '../lib/auth/useIsAdmin';
import {
  approvePendingThunk,
  fetchPendingByDate,
  rejectPendingThunk,
  updatePendingThunk,
} from '../store/thunks/pending-thunks';
import { decodeEnvelope } from '../lib/content/envelope-builder';
import { CitationLine, type CitationSource, type MentionedGame } from './CitationLine';
import type {
  PendingContentItem,
  PendingItemStatus,
} from '../lib/api/pending-api-client';

/**
 * `<AdminPendingForDate>` — admin-only surface that renders every
 * queue item whose target publish date equals the viewed day
 * regardless of status (pending / approved / rejected /
 * system-auto-approved / denied). Landed 2026-09-12 alongside the
 * audit `targetDate` fix + the widened `ListPendingContentV1`
 * date-filter so admins can completely control any date's queue
 * content without a separate endpoint.
 *
 * Rendered below the main {@link DailyLayout} on both
 * `<DailyPage>` and `<DayDetailPage>`. Standard viewers see
 * nothing.
 *
 * Function-length budget: every function ≤20 lines per R3.7.
 */

export interface AdminPendingForDateProps {
  /** The `YYYY-MM-DD` date the calling page is showing. */
  readonly viewedDate: string;
}

const EMPTY_ITEMS: ReadonlyArray<PendingContentItem> = Object.freeze([]);

export function AdminPendingForDate(props: AdminPendingForDateProps): JSX.Element | null {
  const isAdmin = useIsAdmin();
  const dispatch = useAppDispatch();
  const entry = useAppSelector((state) => state.pending?.byDate?.[props.viewedDate]);
  const items = entry?.items ?? EMPTY_ITEMS;
  const status = entry?.status ?? 'idle';
  const actionError = useAppSelector((state) => state.pending?.actionError);
  useEffect(() => {
    if (isAdmin && props.viewedDate.length === 10 && status === 'idle') {
      void dispatch(fetchPendingByDate(props.viewedDate));
    }
  }, [dispatch, isAdmin, props.viewedDate, status]);
  if (!isAdmin) return null;
  if (items.length === 0 && status !== 'failed') return null;
  const sorted = [...items].sort(compareForDayView);
  return (
    <Box
      sx={{
        mt: 4, p: 2, borderRadius: 1,
        border: '1px dashed', borderColor: 'warning.main',
        bgcolor: 'warning.50',
      }}
    >
      <Typography variant="overline" color="text.secondary">Admin only</Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>Queue for this day</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {sorted.length} item{sorted.length === 1 ? '' : 's'} whose target publish day is {props.viewedDate} — every status shown.
      </Typography>
      {status === 'failed' && entry?.error !== undefined && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load queue: {entry.error}</Alert>
      )}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
      <Stack spacing={2}>
        {sorted.map((it) => (
          <QueueRow key={it.id} item={it} />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Sort order for the admin day-view: pending first (needs action),
 * then approved (published), then system-auto-approved, then
 * rejected / denied (terminal negative). Within each status, rank
 * ascending (lowest rank = highest impact) then ingestedAt ascending.
 */
function compareForDayView(a: PendingContentItem, b: PendingContentItem): number {
  const statusRank: Record<PendingItemStatus, number> = {
    pending: 0,
    approved: 1,
    'system-auto-approved': 2,
    rejected: 3,
    denied: 4,
  };
  const sd = statusRank[a.status] - statusRank[b.status];
  if (sd !== 0) return sd;
  const rankA = extractRank(a) ?? Number.MAX_SAFE_INTEGER;
  const rankB = extractRank(b) ?? Number.MAX_SAFE_INTEGER;
  if (rankA !== rankB) return rankA - rankB;
  return a.ingestedAt.localeCompare(b.ingestedAt);
}

function extractRank(item: PendingContentItem): number | undefined {
  const envelope = decodeEnvelope(item.body);
  const raw = (envelope as { rank?: unknown }).rank;
  return typeof raw === 'number' && raw > 0 ? raw : undefined;
}

/**
 * Chip color for a per-item status pill. Pending is neutral; the
 * approved forks are info/success; the rejected forks are error.
 */
function statusChipColor(status: PendingItemStatus): 'default' | 'primary' | 'success' | 'info' | 'error' | 'warning' {
  if (status === 'pending') return 'warning';
  if (status === 'approved') return 'success';
  if (status === 'system-auto-approved') return 'info';
  if (status === 'rejected') return 'error';
  if (status === 'denied') return 'error';
  return 'default';
}

function QueueRow({ item }: { readonly item: PendingContentItem }): JSX.Element {
  const dispatch = useAppDispatch();
  const approveStatus = useAppSelector((state) => state.pending?.approveStatus ?? 'idle');
  const rejectStatus = useAppSelector((state) => state.pending?.rejectStatus ?? 'idle');
  const busy = approveStatus === 'loading' || rejectStatus === 'loading';
  const envelope = decodeEnvelope(item.body);
  const rank = extractRank(item);
  const sources = buildSources(envelope);
  const games = buildMentionedGames(envelope);
  const isActionable = item.status === 'pending';
  const [editing, setEditing] = useState<boolean>(false);
  return (
    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <Chip label={item.type} size="small" color="primary" />
        <Chip label={item.status} size="small" color={statusChipColor(item.status)} />
        {rank !== undefined && <Chip label={`rank #${rank}`} size="small" color={rank <= 3 ? 'success' : 'default'} variant="outlined" />}
        <Chip label={`from ${item.source}`} size="small" variant="outlined" />
      </Stack>
      {envelope.title !== undefined && (
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>{envelope.title}</Typography>
      )}
      {envelope.image !== undefined && (
        <Box
          component="img"
          src={envelope.image}
          alt={envelope.title || 'queue image'}
          sx={{ float: 'left', maxWidth: '40%', maxHeight: 180, objectFit: 'contain', borderRadius: 1, mr: 2, mb: 1 }}
        />
      )}
      {envelope.body !== undefined && <Typography variant="body2">{envelope.body}</Typography>}
      <CitationLine hashKey={item.id} sources={sources} mentionedGames={games} />
      {isActionable && (
        <Stack direction="row" spacing={1} sx={{ mt: 2, clear: 'both' }}>
          <Button variant="contained" color="success" size="small"
            onClick={() => void dispatch(approvePendingThunk(item.id))} disabled={busy}>
            Approve
          </Button>
          <Button variant="outlined" color="primary" size="small"
            onClick={() => setEditing((s) => !s)} disabled={busy}>
            {editing ? 'Cancel edit' : 'Edit'}
          </Button>
          <Button variant="outlined" color="error" size="small"
            onClick={() => {
              const reason = window.prompt('Rejection reason (1-500 chars):');
              if (reason && reason.trim().length > 0) {
                void dispatch(rejectPendingThunk(item.id, reason.trim()));
              }
            }} disabled={busy}>
            Reject
          </Button>
        </Stack>
      )}
      {editing && isActionable && (
        <EditPanel item={item} onClose={() => setEditing(false)} />
      )}
    </Box>
  );
}

/**
 * Inline edit panel for a pending queue row (2026-09-13
 * edit-in-queue). Presents the envelope as a JSON textarea so the
 * admin can adjust any field (title, body, image, sources,
 * corroboratingSources, etc.) and save via
 * `POST /UpdatePendingContentV1`. Optimistic-concurrency via
 * `expectedVersion` — a 409 response prompts the admin to close +
 * re-open the panel to pick up the fresh row.
 */
function EditPanel({
  item, onClose,
}: {
  readonly item: PendingContentItem;
  readonly onClose: () => void;
}): JSX.Element {
  const dispatch = useAppDispatch();
  const [draft, setDraft] = useState<string>(() => prettyPrint(item.body));
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const save = async (): Promise<void> => {
    setSaving(true);
    setError(undefined);
    let canonical: string;
    try {
      canonical = JSON.stringify(JSON.parse(draft));
    } catch {
      setError('Draft is not valid JSON.');
      setSaving(false);
      return;
    }
    const result = await dispatch(updatePendingThunk({
      id: item.id,
      contentType: item.type,
      body: canonical,
      expectedVersion: item.version,
    }));
    setSaving(false);
    if (result.ok) {
      onClose();
    } else {
      setError(result.error ?? 'Save failed. Try re-opening the editor to pick up the latest row.');
    }
  };
  return (
    <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'primary.main' }}>
      <Typography variant="overline" color="text.secondary">Envelope JSON — edit and save</Typography>
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        multiline
        fullWidth
        minRows={8}
        sx={{ mt: 1, fontFamily: 'monospace' }}
      />
      {error !== undefined && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button variant="contained" size="small" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save edits'}
        </Button>
        <Button variant="text" size="small" onClick={onClose} disabled={saving}>Cancel</Button>
      </Stack>
    </Box>
  );
}

function prettyPrint(raw: string): string {
  try { return JSON.stringify(JSON.parse(raw), null, 2); }
  catch { return raw; }
}

function buildSources(envelope: ReturnType<typeof decodeEnvelope>): ReadonlyArray<CitationSource> {
  const out: CitationSource[] = [];
  if (typeof envelope.source === 'string' && envelope.source.length > 0) {
    out.push({ name: envelope.source, url: envelope.sourceUrl ?? '' });
  }
  const cs = (envelope as { corroboratingSources?: unknown }).corroboratingSources;
  if (Array.isArray(cs)) {
    for (const e of cs) {
      if (e === null || typeof e !== 'object') continue;
      const s = (e as Record<string, unknown>)['source'];
      const u = (e as Record<string, unknown>)['url'];
      if (typeof s === 'string' && typeof u === 'string' && s.length > 0 && u.length > 0) {
        out.push({ name: s, url: u });
      }
    }
  }
  return out;
}

function buildMentionedGames(envelope: ReturnType<typeof decodeEnvelope>): ReadonlyArray<MentionedGame> {
  const raw = (envelope as { mentionedGames?: unknown }).mentionedGames;
  if (!Array.isArray(raw)) return [];
  const out: MentionedGame[] = [];
  for (const e of raw) {
    if (e === null || typeof e !== 'object') continue;
    const g = (e as Record<string, unknown>)['gameId'];
    const t = (e as Record<string, unknown>)['title'];
    if (typeof g === 'string' && g.length > 0 && typeof t === 'string' && t.length > 0) {
      out.push({ gameId: g, title: t });
    }
  }
  return out;
}
